import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  cancelOrder,
  deleteOrder,
  listOrders,
  listUsers,
  respondToQuotation,
  updateOrderStatus,
} from "../services/adminDataService";

const cacheMs = 60 * 1000;

const createResource = () => ({
  items: [],
  status: "idle",
  error: "",
  lastFetched: 0,
});

const shouldSkipFetch = (resource, force) => {
  if (force) return false;
  if (resource.status === "loading") return true;

  return resource.lastFetched && Date.now() - resource.lastFetched < cacheMs;
};

const getErrorMessage = (error, fallback) => error?.message || fallback;

const withoutCacheOptions = (params = {}) => {
  const { force, ...queryParams } = params;

  return queryParams;
};

export const fetchOrders = createAsyncThunk(
  "adminData/fetchOrders",
  async (params = {}) => listOrders({ page: 1, limit: 100, ...withoutCacheOptions(params) }),
  {
    condition: (params = {}, { getState }) => {
      const force = Boolean(params.force);
      const { orders } = getState().adminData;

      return !shouldSkipFetch(orders, force);
    },
  },
);

export const fetchUsers = createAsyncThunk(
  "adminData/fetchUsers",
  async (params = {}) => listUsers({ page: 1, limit: 100, ...withoutCacheOptions(params) }),
  {
    condition: (params = {}, { getState }) => {
      const force = Boolean(params.force);
      const { users } = getState().adminData;

      return !shouldSkipFetch(users, force);
    },
  },
);

export const updateOrderStatusCached = createAsyncThunk(
  "adminData/updateOrderStatus",
  async ({ id, status }) => {
    await updateOrderStatus(id, status);

    return { id, status };
  },
);

export const cancelOrderCached = createAsyncThunk(
  "adminData/cancelOrder",
  async (id) => {
    await cancelOrder(id);

    return id;
  },
);

export const deleteOrderCached = createAsyncThunk(
  "adminData/deleteOrder",
  async (id) => {
    await deleteOrder(id);

    return id;
  },
);

export const respondToQuotationCached = createAsyncThunk(
  "adminData/respondToQuotation",
  async ({ quotationId, payload }) => {
    await respondToQuotation(quotationId, payload);

    return quotationId;
  },
);

const adminDataSlice = createSlice({
  name: "adminData",
  initialState: {
    orders: createResource(),
    users: createResource(),
  },
  reducers: {
    clearAdminDataCache: (state) => {
      state.orders.lastFetched = 0;
      state.users.lastFetched = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.orders.status = "loading";
        state.orders.error = "";
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.orders.items = action.payload;
        state.orders.status = "succeeded";
        state.orders.lastFetched = Date.now();
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        if (action.meta.condition) return;

        state.orders.status = "failed";
        state.orders.error = getErrorMessage(action.error, "Unable to load orders.");
      })
      .addCase(fetchUsers.pending, (state) => {
        state.users.status = "loading";
        state.users.error = "";
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.users.items = action.payload;
        state.users.status = "succeeded";
        state.users.lastFetched = Date.now();
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        if (action.meta.condition) return;

        state.users.status = "failed";
        state.users.error = getErrorMessage(action.error, "Unable to load users.");
      })
      .addCase(updateOrderStatusCached.fulfilled, (state, action) => {
        const order = state.orders.items.find((item) => item.id === action.payload.id);

        if (order) order.status = action.payload.status;
      })
      .addCase(cancelOrderCached.fulfilled, (state, action) => {
        const order = state.orders.items.find((item) => item.id === action.payload);

        if (order) order.status = "cancelled";
      })
      .addCase(deleteOrderCached.fulfilled, (state, action) => {
        state.orders.items = state.orders.items.filter(
          (order) => order.id !== action.payload,
        );
      })
      .addCase(respondToQuotationCached.fulfilled, () => {});
  },
});

export const { clearAdminDataCache } = adminDataSlice.actions;

export default adminDataSlice.reducer;
